import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@/auth/SessionProvider";
import { apiGet } from "@/lib/apiClient";
import { Building2, Search } from "lucide-react";

type AccountRow = {
  id?: string;
  domain: string;
  companyName: string;
  industry?: string | null;
  plan?: string | null;
  status: "active" | "trial" | "canceled";
  seats?: number | null;
  createdAt?: string;
};

type AccountsResponse = {
  accounts?: AccountRow[];
};

function statusClass(status: string): string {
  if (status === "active") return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
  if (status === "trial") return "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100";
  if (status === "canceled") return "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200";
  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";
}

export function OrganizationOverviewPage() {
  const session = useSession();
  const isSuperAdmin = session.data?.user?.role === "super_admin";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const res = await apiGet<AccountsResponse>("/api/accounts");
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error || "Could not load organizations.");
        setAccounts([]);
      } else {
        setError(null);
        setAccounts(res.data?.accounts ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(
      (a) =>
        a.domain.toLowerCase().includes(q) ||
        a.companyName.toLowerCase().includes(q) ||
        (a.industry || "").toLowerCase().includes(q) ||
        (a.plan || "").toLowerCase().includes(q),
    );
  }, [accounts, searchQuery]);

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div className="flex flex-col gap-2 border-b border-slate-200/80 pb-6 dark:border-white/10 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Organization</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
            {isSuperAdmin
              ? "Tenant organizations from /api/accounts (read-only on Pages; create/edit stays on Amplify for now)."
              : "Your organization record from /api/accounts."}
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-white/10 dark:bg-slate-950/40">
          <Search className="h-4 w-4 text-slate-400" aria-hidden />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search domain, company, plan…"
            className="w-full min-w-[12rem] bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
          />
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading organizations…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {!loading && !error ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-950/40">
          <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-white/10">
            <Building2 className="h-5 w-5 text-indigo-600" aria-hidden />
            <span className="text-sm font-semibold text-slate-900 dark:text-white">
              Organizations ({filtered.length})
            </span>
          </div>
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-white/10 dark:bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 font-semibold">Company</th>
                <th className="px-4 py-3 font-semibold">Domain</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Seats</th>
                <th className="px-4 py-3 font-semibold">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    No organizations match your search.
                  </td>
                </tr>
              ) : (
                filtered.map((account) => (
                  <tr key={account.id || account.domain} className="border-b border-slate-100 dark:border-white/5">
                    <td className="px-4 py-3 font-medium">
                      <Link
                        to={`/organization/${encodeURIComponent(account.domain)}`}
                        className="text-indigo-600 hover:underline dark:text-indigo-300"
                      >
                        {account.companyName}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">{account.domain}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{account.plan || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusClass(account.status)}`}
                      >
                        {account.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{account.seats ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
